import { useState, FormEvent, useRef, useEffect } from "react";
import { useTheme } from "../../../contexts/ThemeContext";
import { useAuth } from "../../../contexts/AuthContext";
import { supabase } from "../../../lib/supabase";
import CategoryPickerModal from "../../../components/feature/CategoryPickerModal";

type Step = "signin" | "signup" | "verify_signup" | "verify_2fa" | "forgot_password" | "reset_sent";

interface AuthFormProps {
  onSuccess: () => void;
  intent?: string | null;
}

// ─── Password validation ───────────────────────────────────────────────────────
function validatePassword(pwd: string): string | null {
  if (pwd.length < 8) return "At least 8 characters required";
  if (!/[0-9]/.test(pwd)) return "Must contain at least 1 number";
  if (!/[^a-zA-Z0-9]/.test(pwd)) return "Must contain at least 1 special character";
  return null;
}

function getPasswordStrength(pwd: string): { score: number; label: string; color: string } {
  if (!pwd) return { score: 0, label: "", color: "" };
  let score = 0;
  if (pwd.length >= 8) score++;
  if (/[0-9]/.test(pwd)) score++;
  if (/[^a-zA-Z0-9]/.test(pwd)) score++;
  if (pwd.length >= 12) score++;
  const labels = ["", "Weak", "Fair", "Good", "Strong"];
  const colors = ["", "bg-red-500", "bg-amber-400", "bg-emerald-400", "bg-emerald-500"];
  return { score, label: labels[score], color: colors[score] };
}

// ─── OTP Input (6-digit boxes) ─────────────────────────────────────────────────
function OtpInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { isDark } = useTheme();
  const inputs = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (i: number, v: string) => {
    const digit = v.replace(/\D/g, "").slice(-1);
    const arr = value.split("").concat(Array(6).fill("")).slice(0, 6);
    arr[i] = digit;
    const joined = arr.join("");
    onChange(joined);
    if (digit && i < 5) inputs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      const arr = value.split("").concat(Array(6).fill("")).slice(0, 6);
      if (arr[i]) {
        arr[i] = "";
        onChange(arr.join(""));
      } else if (i > 0) {
        arr[i - 1] = "";
        onChange(arr.join(""));
        inputs.current[i - 1]?.focus();
      }
    }
    if (e.key === "ArrowLeft" && i > 0) inputs.current[i - 1]?.focus();
    if (e.key === "ArrowRight" && i < 5) inputs.current[i + 1]?.focus();
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    onChange(pasted.padEnd(6, "").slice(0, 6));
    e.preventDefault();
    inputs.current[Math.min(pasted.length, 5)]?.focus();
  };

  return (
    <div className="flex gap-2 justify-center my-4">
      {Array.from({ length: 6 }).map((_, i) => (
        <input
          key={i}
          ref={(el) => { inputs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[i] || ""}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={i === 0 ? handlePaste : undefined}
          className={`w-11 h-12 text-center rounded-xl border font-syne font-bold text-lg outline-none transition-all duration-150 focus:ring-2 ${
            isDark
              ? "bg-[#1a1a1a] border-white/10 text-white focus:ring-white/20 focus:border-white/30"
              : "bg-[#f0f0f0] border-black/10 text-[#111] focus:ring-black/10 focus:border-black/25"
          }`}
        />
      ))}
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function AuthForm({ onSuccess, intent }: AuthFormProps) {
  const [step, setStep] = useState<Step>(intent === "pro" ? "signup" : "signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [otp, setOtp] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);

  const { signIn, signUp, verifySignupOtp, verify2FA, send2FAOTP, resendOtp, pendingTwoFA, twoFaEmail } = useAuth();
  const { isDark } = useTheme();

  const strength = getPasswordStrength(password);

  // ─── Auto-send OTP when 2FA is triggered ────────────────────────────────
  useEffect(() => {
    if (pendingTwoFA && twoFaEmail) {
      setEmail(twoFaEmail);
      setOtp("");
      setError(null);
      setSendingCode(true);
      send2FAOTP().then(({ error: err }) => {
        setSendingCode(false);
        if (err) {
          setError(err.message || "Failed to send code. Please try again.");
        } else {
          setStep("verify_2fa");
          startCooldown();
        }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingTwoFA, twoFaEmail]);

  // ─── Resend cooldown timer ─────────────────────────────────────────────────
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const COOLDOWN_DURATION = 15;

  const startCooldown = () => {
    if (cooldownRef.current) return;
    setResendCooldown(COOLDOWN_DURATION);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current!);
          cooldownRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const handleSignIn = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const result = await signIn(email, password);
    if (result.error) {
      setError(result.error.message);
    } else if (!result.requires2FA) {
      onSuccess();
    }
    // If requires2FA, the useEffect watching pendingTwoFA sends the code and transitions
    setLoading(false);
  };

  const handleSignUp = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const pwErr = validatePassword(password);
    if (pwErr) { setError(pwErr); return; }
    setLoading(true);
    const { error: err } = await signUp(email, password);
    if (err) {
      setError(err.message);
    } else {
      setOtp("");
      setStep("verify_signup");
      startCooldown();
    }
    setLoading(false);
  };

  const handleVerifySignup = async (e: FormEvent) => {
    e.preventDefault();
    if (otp.replace(/\D/g, "").length < 6) { setError("Enter the full 6-digit code."); return; }
    setError(null);
    setLoading(true);
    const { error: err } = await verifySignupOtp(email, otp.replace(/\D/g, ""));
    if (err) {
      setError("Invalid or expired code. Check your email or request a new one.");
    } else {
      setShowCategoryPicker(true);
    }
    setLoading(false);
  };

  const handleVerify2FA = async (e: FormEvent) => {
    e.preventDefault();
    if (otp.replace(/\D/g, "").length < 6) { setError("Enter the full 6-digit code."); return; }
    setError(null);
    setLoading(true);
    const { error: err } = await verify2FA(email, otp.replace(/\D/g, ""));
    if (err) {
      setError("Invalid or expired code. Try requesting a new one.");
    } else {
      onSuccess();
    }
    setLoading(false);
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || resendLoading) return;
    setResendLoading(true);
    setError(null);
    const type = step === "verify_signup" ? "signup" : "2fa";
    const { error: err } = await resendOtp(email, type);
    setResendLoading(false);
    if (err) {
      setError(err.message || "Failed to resend. Try again shortly.");
      return;
    }
    startCooldown();
  };

  const handleForgotPassword = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) { setError("Enter your email address first."); return; }
    setError(null);
    setLoading(true);
    const redirectTo = `${window.location.origin}/dashboard`;
    const { error: err } = await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo });
    if (err) {
      setError(err.message);
    } else {
      setStep("reset_sent");
    }
    setLoading(false);
  };

  // ─── Shared styles ────────────────────────────────────────────────────────
  const inputClass = `w-full border rounded-xl px-4 py-3 font-inter text-sm focus:outline-none focus:ring-2 transition-all duration-150 ${
    isDark
      ? "bg-white/5 border-white/10 text-white placeholder:text-[#444] focus:ring-white/15 focus:border-white/25"
      : "bg-white border-black/10 text-[#111] placeholder:text-[#aaa] focus:ring-black/8 focus:border-black/25"
  }`;

  const submitBtnClass = `w-full font-inter font-medium text-sm py-3 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed mt-1 ${
    isDark ? "bg-white text-black hover:bg-[#e0e0e0]" : "bg-[#111] text-white hover:bg-[#333]"
  }`;

  // ─── Sending code loading screen ──────────────────────────────────────────
  if (sendingCode) {
    return (
      <div className="flex flex-col items-center justify-center py-10 gap-4">
        <i className={`ri-loader-4-line text-2xl animate-spin ${isDark ? "text-white/40" : "text-black/30"}`} />
        <p className={`font-inter text-sm ${isDark ? "text-[#666]" : "text-[#aaa]"}`}>Sending verification code…</p>
      </div>
    );
  }

  // ─── Verify Signup OTP ────────────────────────────────────────────────────
  if (step === "verify_signup") {
    return (
      <>
        <div>
          <div className="text-center mb-6">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${isDark ? "bg-white/5" : "bg-black/5"}`}>
              <i className={`ri-mail-check-line text-xl ${isDark ? "text-white/60" : "text-[#666]"}`} />
            </div>
            <h3 className={`font-syne font-semibold text-lg mb-1.5 ${isDark ? "text-white" : "text-[#111]"}`}>
              Check your email
            </h3>
            <p className={`font-inter text-sm leading-relaxed ${isDark ? "text-[#888]" : "text-[#666]"}`}>
              We sent a 6-digit verification code to<br />
              <strong className={isDark ? "text-white" : "text-[#111]"}>{email}</strong>
            </p>
          </div>
          <form onSubmit={handleVerifySignup} className="flex flex-col gap-3">
            <OtpInput value={otp} onChange={setOtp} />
            {error && <p className="font-inter text-xs text-red-400 text-center">{error}</p>}
            <button type="submit" disabled={loading || otp.replace(/\D/g, "").length < 6} className={submitBtnClass}>
              {loading ? "Verifying…" : "Verify & Continue"}
            </button>
          </form>
          <div className="flex items-center justify-between mt-4">
            <button
              type="button"
              onClick={() => { setStep("signup"); setError(null); setOtp(""); }}
              className={`font-inter text-xs cursor-pointer ${isDark ? "text-[#555] hover:text-[#888]" : "text-[#bbb] hover:text-[#888]"}`}
            >
              &larr; Back
            </button>
            <button
              type="button"
              onClick={handleResend}
              disabled={resendCooldown > 0 || resendLoading}
              className={`font-inter text-xs cursor-pointer transition-colors ${
                resendCooldown > 0 || resendLoading
                  ? isDark ? "text-[#444]" : "text-[#ccc]"
                  : isDark ? "text-[#777] hover:text-white" : "text-[#888] hover:text-[#111]"
              }`}
            >
              {resendLoading ? "Sending…" : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
            </button>
          </div>
        </div>
        {showCategoryPicker && (
          <CategoryPickerModal
            onComplete={() => { setShowCategoryPicker(false); onSuccess(); }}
            onSkip={() => { setShowCategoryPicker(false); onSuccess(); }}
          />
        )}
      </>
    );
  }

  // ─── 2FA Challenge ────────────────────────────────────────────────────────
  if (step === "verify_2fa") {
    const circumference = 2 * Math.PI * 20;
    const dashOffset = circumference - (resendCooldown / COOLDOWN_DURATION) * circumference;

    return (
      <div>
        <div className="text-center mb-6">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${isDark ? "bg-white/5" : "bg-black/5"}`}>
            <i className={`ri-mail-check-line text-xl ${isDark ? "text-white/60" : "text-[#666]"}`} />
          </div>
          <h3 className={`font-syne font-semibold text-lg mb-1.5 ${isDark ? "text-white" : "text-[#111]"}`}>
            Check your email
          </h3>
          <p className={`font-inter text-sm leading-relaxed ${isDark ? "text-[#888]" : "text-[#666]"}`}>
            A 6-digit code was sent to<br />
            <strong className={isDark ? "text-white" : "text-[#111]"}>{email}</strong>
          </p>
        </div>
        <form onSubmit={handleVerify2FA} className="flex flex-col gap-3">
          <OtpInput value={otp} onChange={setOtp} />
          {error && <p className="font-inter text-xs text-red-400 text-center">{error}</p>}
          <button type="submit" disabled={loading || otp.replace(/\D/g, "").length < 6} className={submitBtnClass}>
            {loading ? "Verifying…" : "Confirm & Sign In"}
          </button>
        </form>

        {/* Resend section */}
        <div className={`mt-5 rounded-xl p-4 flex flex-col items-center gap-3 ${isDark ? "bg-white/[0.03] border border-white/[0.06]" : "bg-black/[0.025] border border-black/[0.06]"}`}>
          {resendCooldown > 0 ? (
            <div className="flex items-center gap-3">
              <div className="relative w-11 h-11 flex-shrink-0">
                <svg className="w-11 h-11 -rotate-90" viewBox="0 0 48 48">
                  <circle cx="24" cy="24" r="20" fill="none" strokeWidth="3" className={isDark ? "stroke-white/8" : "stroke-black/8"} />
                  <circle
                    cx="24" cy="24" r="20" fill="none" strokeWidth="3" strokeLinecap="round"
                    strokeDasharray={circumference} strokeDashoffset={dashOffset}
                    className={`transition-all duration-1000 ${isDark ? "stroke-white/40" : "stroke-black/30"}`}
                  />
                </svg>
                <span className={`absolute inset-0 flex items-center justify-center font-inter font-semibold text-sm ${isDark ? "text-white/60" : "text-[#555]"}`}>
                  {resendCooldown}
                </span>
              </div>
              <div>
                <p className={`font-inter text-sm font-medium ${isDark ? "text-white/70" : "text-[#444]"}`}>
                  Didn&apos;t get a code?
                </p>
                <p className={`font-inter text-xs ${isDark ? "text-[#555]" : "text-[#aaa]"}`}>
                  Resend available in {resendCooldown}s
                </p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 w-full">
              <p className={`font-inter text-sm ${isDark ? "text-[#888]" : "text-[#666]"}`}>
                Didn&apos;t receive a code?
              </p>
              <button
                type="button"
                onClick={handleResend}
                disabled={resendLoading}
                className={`w-full font-inter text-sm font-medium py-2.5 rounded-full border cursor-pointer transition-all duration-200 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed ${
                  isDark ? "border-white/15 text-white hover:bg-white/8" : "border-black/15 text-[#111] hover:bg-black/5"
                }`}
              >
                {resendLoading ? (
                  <><i className="ri-loader-4-line mr-2 text-sm animate-spin" />Sending…</>
                ) : (
                  <><i className="ri-mail-send-line mr-2 text-sm" />Resend code</>
                )}
              </button>
            </div>
          )}
          <p className={`font-inter text-[11px] text-center ${isDark ? "text-[#444]" : "text-[#bbb]"}`}>
            Check your spam or junk folder if you don&apos;t see it
          </p>
        </div>

        <div className="flex items-center justify-start mt-4">
          <button
            type="button"
            onClick={() => { setStep("signin"); setError(null); setOtp(""); setResendCooldown(0); if (cooldownRef.current) { clearInterval(cooldownRef.current); cooldownRef.current = null; } }}
            className={`font-inter text-xs cursor-pointer ${isDark ? "text-[#555] hover:text-[#888]" : "text-[#bbb] hover:text-[#888]"}`}
          >
            &larr; Use a different account
          </button>
        </div>
      </div>
    );
  }

  // ─── Forgot Password ──────────────────────────────────────────────────────
  if (step === "forgot_password") {
    return (
      <div>
        <button
          type="button"
          onClick={() => { setStep("signin"); setError(null); }}
          className={`flex items-center gap-1.5 font-inter text-xs mb-6 cursor-pointer transition-colors ${isDark ? "text-[#555] hover:text-[#888]" : "text-[#bbb] hover:text-[#777]"}`}
        >
          <i className="ri-arrow-left-line text-xs" />
          Back to sign in
        </button>

        <div className="mb-6">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${isDark ? "bg-white/5" : "bg-black/5"}`}>
            <i className={`ri-lock-2-line text-xl ${isDark ? "text-white/60" : "text-[#666]"}`} />
          </div>
          <h3 className={`font-syne font-semibold text-lg mb-1.5 ${isDark ? "text-white" : "text-[#111]"}`}>
            Reset your password
          </h3>
          <p className={`font-inter text-sm leading-relaxed ${isDark ? "text-[#888]" : "text-[#666]"}`}>
            Enter your account email and we&apos;ll send you a reset link.
          </p>
        </div>

        <form onSubmit={handleForgotPassword} className="flex flex-col gap-3">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            className={inputClass}
            autoFocus
          />
          {error && <p className="font-inter text-xs text-red-400 px-1">{error}</p>}
          <button type="submit" disabled={loading || !email.trim()} className={submitBtnClass}>
            {loading ? "Sending…" : "Send Reset Link"}
          </button>
        </form>
      </div>
    );
  }

  // ─── Reset Email Sent ────────────────────────────────────────────────────
  if (step === "reset_sent") {
    return (
      <div className="text-center py-4">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${isDark ? "bg-white/5" : "bg-black/5"}`}>
          <i className={`ri-mail-send-line text-xl ${isDark ? "text-white/60" : "text-[#666]"}`} />
        </div>
        <h3 className={`font-syne font-semibold text-lg mb-2 ${isDark ? "text-white" : "text-[#111]"}`}>
          Check your inbox
        </h3>
        <p className={`font-inter text-sm leading-relaxed mb-2 ${isDark ? "text-[#888]" : "text-[#666]"}`}>
          A password reset link was sent to
        </p>
        <p className={`font-inter text-sm font-semibold mb-6 ${isDark ? "text-white" : "text-[#111]"}`}>
          {email}
        </p>
        <p className={`font-inter text-xs mb-6 ${isDark ? "text-[#555]" : "text-[#aaa]"}`}>
          Click the link in the email to set a new password. Check your spam folder if you don&apos;t see it.
        </p>
        <button
          type="button"
          onClick={() => { setStep("signin"); setError(null); }}
          className={`font-inter text-sm font-medium cursor-pointer underline underline-offset-2 transition-colors ${isDark ? "text-white/50 hover:text-white" : "text-[#888] hover:text-[#111]"}`}
        >
          Back to sign in
        </button>
      </div>
    );
  }

  // ─── Sign In / Sign Up ────────────────────────────────────────────────────
  return (
    <div>
      {intent === "pro" && (
        <div className={`rounded-xl px-4 py-3 mb-6 border ${isDark ? "bg-amber-500/8 border-amber-500/20" : "bg-amber-50 border-amber-200"}`}>
          <p className={`font-inter text-sm ${isDark ? "text-amber-400" : "text-amber-700"}`}>
            <i className="ri-vip-crown-line mr-2" />
            Sign in or create an account to proceed to Pro checkout.
          </p>
        </div>
      )}

      {/* Mode Toggle */}
      <div className={`flex rounded-full p-1 mb-6 ${isDark ? "bg-white/5" : "bg-black/5"}`}>
        {(["signin", "signup"] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => { setStep(m); setError(null); setPassword(""); setAgreedToTerms(false); setAgreedToPrivacy(false); }}
            className={`flex-1 font-inter text-sm font-medium py-1.5 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap ${
              step === m
                ? isDark ? "bg-white text-black" : "bg-[#111] text-white"
                : isDark ? "text-[#888] hover:text-white" : "text-[#888] hover:text-[#111]"
            }`}
          >
            {m === "signin" ? "Sign In" : "Create Account"}
          </button>
        ))}
      </div>

      <form onSubmit={step === "signin" ? handleSignIn : handleSignUp} className="flex flex-col gap-3">
        <input
          type="email"
          name="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email address"
          className={inputClass}
        />

        {/* Password field with show/hide toggle */}
        <div className={`flex items-center gap-2 w-full px-4 py-3 rounded-xl border transition-all duration-150 focus-within:ring-2 ${
          isDark
            ? "bg-white/5 border-white/10 focus-within:ring-white/15 focus-within:border-white/25"
            : "bg-white border-black/10 focus-within:ring-black/8 focus-within:border-black/25"
        }`}>
          <input
            type={showPassword ? "text" : "password"}
            name="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={step === "signup" ? "Create a password" : "Password"}
            className={`flex-1 bg-transparent outline-none font-inter text-sm ${isDark ? "text-white placeholder:text-[#444]" : "text-[#111] placeholder:text-[#aaa]"}`}
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className={`w-4 h-4 flex items-center justify-center cursor-pointer flex-shrink-0 ${isDark ? "text-[#444] hover:text-[#888]" : "text-[#ccc] hover:text-[#888]"}`}
          >
            <i className={`${showPassword ? "ri-eye-off-line" : "ri-eye-line"} text-xs`} />
          </button>
        </div>

        {/* Forgot password link — signin only */}
        {step === "signin" && (
          <div className="flex justify-end -mt-1">
            <button
              type="button"
              onClick={() => { setStep("forgot_password"); setError(null); }}
              className={`font-inter text-xs cursor-pointer transition-colors ${isDark ? "text-[#555] hover:text-[#888]" : "text-[#bbb] hover:text-[#777]"}`}
            >
              Forgot password?
            </button>
          </div>
        )}

        {/* Password strength + requirements (signup only) */}
        {step === "signup" && password.length > 0 && (
          <div className="space-y-1.5 px-1">
            <div className="flex items-center gap-2">
              <div className={`flex-1 h-1 rounded-full overflow-hidden ${isDark ? "bg-white/8" : "bg-black/8"}`}>
                <div
                  className={`h-full rounded-full transition-all duration-300 ${strength.color}`}
                  style={{ width: `${(strength.score / 4) * 100}%` }}
                />
              </div>
              {strength.label && (
                <span className={`font-inter text-[10px] ${
                  strength.score <= 1 ? "text-red-400" : strength.score === 2 ? "text-amber-400" : "text-emerald-400"
                }`}>{strength.label}</span>
              )}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-0.5">
              {[
                { ok: password.length >= 8, label: "8+ characters" },
                { ok: /[0-9]/.test(password), label: "1 number" },
                { ok: /[^a-zA-Z0-9]/.test(password), label: "1 special character" },
              ].map((req) => (
                <span key={req.label} className={`font-inter text-[10px] flex items-center gap-1 ${req.ok ? "text-emerald-400" : isDark ? "text-[#555]" : "text-[#bbb]"}`}>
                  <i className={`${req.ok ? "ri-checkbox-circle-line" : "ri-circle-line"} text-[10px]`} />
                  {req.label}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Legal agreement checkboxes — signup only */}
        {step === "signup" && (
          <div className={`flex flex-col gap-2.5 rounded-xl px-4 py-3.5 border ${isDark ? "bg-white/[0.02] border-white/6" : "bg-black/[0.02] border-black/8"}`}>
            <label className="flex items-start gap-3 cursor-pointer group">
              <div className="relative flex-shrink-0 mt-0.5">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="sr-only"
                />
                <div
                  onClick={() => setAgreedToTerms((v) => !v)}
                  className={`w-4 h-4 rounded flex items-center justify-center border transition-all duration-150 cursor-pointer ${
                    agreedToTerms
                      ? isDark ? "bg-white border-white" : "bg-[#111] border-[#111]"
                      : isDark ? "bg-transparent border-white/20 group-hover:border-white/40" : "bg-transparent border-black/20 group-hover:border-black/40"
                  }`}
                >
                  {agreedToTerms && (
                    <i className={`ri-check-line text-[10px] font-bold ${isDark ? "text-black" : "text-white"}`} />
                  )}
                </div>
              </div>
              <span className={`font-inter text-xs leading-relaxed select-none ${isDark ? "text-[#888]" : "text-[#666]"}`}>
                I agree to Trackly&apos;s{" "}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className={`font-medium underline underline-offset-2 transition-colors duration-150 ${isDark ? "text-white hover:text-[#cccccc]" : "text-[#111] hover:text-[#444]"}`}
                >
                  Terms of Service
                </a>
              </span>
            </label>

            <div className={`h-px ${isDark ? "bg-white/5" : "bg-black/6"}`} />

            <label className="flex items-start gap-3 cursor-pointer group">
              <div className="relative flex-shrink-0 mt-0.5">
                <input
                  type="checkbox"
                  checked={agreedToPrivacy}
                  onChange={(e) => setAgreedToPrivacy(e.target.checked)}
                  className="sr-only"
                />
                <div
                  onClick={() => setAgreedToPrivacy((v) => !v)}
                  className={`w-4 h-4 rounded flex items-center justify-center border transition-all duration-150 cursor-pointer ${
                    agreedToPrivacy
                      ? isDark ? "bg-white border-white" : "bg-[#111] border-[#111]"
                      : isDark ? "bg-transparent border-white/20 group-hover:border-white/40" : "bg-transparent border-black/20 group-hover:border-black/40"
                  }`}
                >
                  {agreedToPrivacy && (
                    <i className={`ri-check-line text-[10px] font-bold ${isDark ? "text-black" : "text-white"}`} />
                  )}
                </div>
              </div>
              <span className={`font-inter text-xs leading-relaxed select-none ${isDark ? "text-[#888]" : "text-[#666]"}`}>
                I have read and agree to the{" "}
                <a
                  href="/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className={`font-medium underline underline-offset-2 transition-colors duration-150 ${isDark ? "text-white hover:text-[#cccccc]" : "text-[#111] hover:text-[#444]"}`}
                >
                  Privacy Policy
                </a>
              </span>
            </label>
          </div>
        )}

        {error && <p className="font-inter text-xs text-red-400 px-1">{error}</p>}

        <button
          type="submit"
          disabled={loading || (step === "signup" && (!agreedToTerms || !agreedToPrivacy))}
          className={submitBtnClass}
        >
          {loading
            ? step === "signin" ? "Signing in…" : "Creating account…"
            : step === "signin" ? "Sign In" : "Create Account"}
        </button>
      </form>
    </div>
  );
}
